/**
 * WaypointRenderer - Handles waypoint mesh creation and styling
 */
import * as THREE from 'three';
import { LabelFactory } from './helpers/LabelFactory.js';
import { CoordinateHelper } from './helpers/CoordinateHelper.js';

export class WaypointRenderer {
  constructor(scene, options = {}) {
    this.scene = scene;
    this.waypointMeshes = [];
    this.viaPointMeshes = [];
    this.pathLines = [];
    this.labelsVisible = options.labelsVisible ?? true;
    this.sphereSizeMultiplier = options.sphereSizeMultiplier ?? 1.0;
  }

  /**
   * Get process type color
   * @param {string} processType - Process type name
   * @returns {number} Three.js color value
   */
  static getProcessTypeColor(processType) {
    const colors = {
      'Start': 0x00ff00,    // Green
      'End': 0xff0000,      // Red
      'Approach': 0xffff00, // Yellow
      'Depart': 0xff8800,   // Orange
    };
    return colors[processType] || 0x00ff00;
  }

  /**
   * Calculate average distance between consecutive waypoints
   * @param {Array} waypoints - Array of waypoints
   * @returns {number} Average distance
   */
  calculateAverageDistance(waypoints) {
    if (waypoints.length < 2) return 0.1;
    
    let totalDistance = 0;
    let count = 0;
    
    for (let i = 0; i < waypoints.length - 1; i++) {
      const p1 = waypoints[i].position;
      const p2 = waypoints[i + 1].position;
      const dist = Math.sqrt(
        Math.pow(p2.x - p1.x, 2) +
        Math.pow(p2.y - p1.y, 2) +
        Math.pow(p2.z - p1.z, 2)
      );
      totalDistance += dist;
      count++;
    }
    
    return count > 0 ? totalDistance / count : 0.1;
  }

  /**
   * Calculate adaptive sphere size based on average distance
   * @param {number} avgDistance - Average distance between waypoints
   * @returns {number} Sphere radius
   */
  getAdaptiveSphereSize(avgDistance) {
    if (avgDistance < 0.020) {
      return 0.004;
    } else if (avgDistance < 0.030) {
      return 0.004 + (avgDistance - 0.020) / (0.030 - 0.020) * (0.006 - 0.004);
    } else if (avgDistance < 0.050) {
      return 0.006 + (avgDistance - 0.030) / (0.050 - 0.030) * (0.008 - 0.006);
    } else if (avgDistance < 0.100) {
      return 0.008 + (avgDistance - 0.050) / (0.100 - 0.050) * (0.012 - 0.008);
    } else {
      return 0.015;
    }
  }

  /**
   * Create a 3D mesh for a waypoint
   * @param {Object} waypoint - Waypoint data
   * @param {number} index - Waypoint index
   * @param {number} avgDistance - Average distance between waypoints
   */
  createWaypointMesh(waypoint, index, avgDistance = 0.1) {
    const group = new THREE.Group();

    // Create sphere with adaptive size and user multiplier
    const baseSphereSize = this.getAdaptiveSphereSize(avgDistance);
    const sphereSize = baseSphereSize * this.sphereSizeMultiplier;
    const geometry = new THREE.SphereGeometry(sphereSize, 16, 16);

    const color = WaypointRenderer.getProcessTypeColor(waypoint.processType);

    const material = new THREE.MeshStandardMaterial({
      color: color,
      emissive: color,
      emissiveIntensity: 0.3,
      metalness: 0.5,
      roughness: 0.5
    });

    const sphere = new THREE.Mesh(geometry, material);
    sphere.userData = { isCenterSphere: true };
    group.add(sphere);

    // Create coordinate frame axes
    this.addAxesToGroup(group, this.sphereSizeMultiplier);

    // Position using coordinate helper
    const position = CoordinateHelper.robotToThreeJS(waypoint.position);
    group.position.copy(position);

    // Apply rotation
    CoordinateHelper.applyRobotRotation(group, waypoint.rotation);

    // Add label if visible
    if (this.labelsVisible) {
      const label = LabelFactory.createWaypointLabel(waypoint.number);
      label.position.set(0, 0.08, 0);
      label.name = 'waypointLabel';
      group.add(label);
    }

    // Store metadata
    group.userData = {
      waypointIndex: index,
      waypoint: waypoint,
      isWaypoint: true,
      index: index
    };

    this.scene.add(group);
    this.waypointMeshes.push(group);
    
    return group;
  }

  /**
   * Create a 3D mesh for a via-point
   * @param {Object} viaPoint - Via-point data
   * @param {number} waypointIndex - Parent waypoint index
   * @param {Object} waypoint - Parent waypoint data
   * @param {number} avgDistance - Average distance between waypoints
   */
  createViaPointMesh(viaPoint, waypointIndex, waypoint, avgDistance = 0.1) {
    const group = new THREE.Group();

    // Smaller sphere for via-points
    const baseWaypointSize = this.getAdaptiveSphereSize(avgDistance);
    const waypointSize = baseWaypointSize * this.sphereSizeMultiplier;
    const geometry = new THREE.SphereGeometry(waypointSize * 0.67, 12, 12);

    const color = 0x00ffff; // Cyan

    const material = new THREE.MeshStandardMaterial({
      color: color,
      emissive: color,
      emissiveIntensity: 0.5,
      metalness: 0.5,
      roughness: 0.5,
      transparent: true,
      opacity: 0.8
    });

    const sphere = new THREE.Mesh(geometry, material);
    sphere.userData = { isCenterSphere: true };
    group.add(sphere);

    // Smaller axes for via-points
    this.addAxesToGroup(group, this.sphereSizeMultiplier * 0.67);

    // Position using coordinate helper
    const position = CoordinateHelper.robotToThreeJS(viaPoint.position);
    group.position.copy(position);

    // Apply rotation
    CoordinateHelper.applyRobotRotation(group, viaPoint.rotation);

    // Add label if visible
    if (this.labelsVisible) {
      const label = LabelFactory.createWaypointLabel(`${waypoint.number}v`);
      label.position.set(0, 0.06, 0);
      label.name = 'viaPointLabel';
      group.add(label);
    }

    // Store metadata
    group.userData = {
      isViaPoint: true,
      waypointIndex,
      viaPoint,
      parentWaypoint: waypoint
    };

    this.scene.add(group);
    this.viaPointMeshes.push(group);
    
    return group;
  }

  /**
   * Add coordinate frame axes to a group
   * @param {THREE.Group} group - Group to add axes to
   * @param {number} scale - Scale multiplier
   */
  addAxesToGroup(group, scale = 1.0) {
    const axisLength = 0.1 * scale;
    const axisRadius = 0.004 * scale;

    // X-axis (red)
    const xGeometry = new THREE.CylinderGeometry(axisRadius, axisRadius, axisLength, 8);
    const xMaterial = new THREE.MeshBasicMaterial({ color: 0xff0000 });
    const xAxis = new THREE.Mesh(xGeometry, xMaterial);
    xAxis.rotation.z = -Math.PI / 2;
    xAxis.position.x = axisLength / 2;
    xAxis.userData = { isAxis: true, axisType: 'x' };
    group.add(xAxis);

    // Y-axis (green) - points in -Z direction
    const yGeometry = new THREE.CylinderGeometry(axisRadius, axisRadius, axisLength, 8);
    const yMaterial = new THREE.MeshBasicMaterial({ color: 0x00ff00 });
    const yAxis = new THREE.Mesh(yGeometry, yMaterial);
    yAxis.rotation.x = Math.PI / 2;
    yAxis.position.z = -axisLength / 2;
    yAxis.userData = { isAxis: true, axisType: 'y' };
    group.add(yAxis);

    // Z-axis (blue) - points up
    const zGeometry = new THREE.CylinderGeometry(axisRadius, axisRadius, axisLength, 8);
    const zMaterial = new THREE.MeshBasicMaterial({ color: 0x0000ff });
    const zAxis = new THREE.Mesh(zGeometry, zMaterial);
    zAxis.position.y = axisLength / 2;
    zAxis.userData = { isAxis: true, axisType: 'z' };
    group.add(zAxis);
  }

  /**
   * Draw lines connecting waypoints
   * @param {Array} waypoints - Array of waypoints
   */
  drawPaths(waypoints) {
    if (waypoints.length < 2) return;

    // Draw general path (thin blue line connecting all waypoints)
    const allPoints = this.waypointMeshes.map(mesh => mesh.position.clone());

    const allGeometry = new THREE.BufferGeometry().setFromPoints(allPoints);
    const allMaterial = new THREE.LineBasicMaterial({ 
      color: 0x4488ff,
      linewidth: 1,
      opacity: 0.4,
      transparent: true
    });

    const allLine = new THREE.Line(allGeometry, allMaterial);
    this.scene.add(allLine);
    this.pathLines.push(allLine);

    // Draw thick lines between waypoints in the same parent node
    this.drawNodeConnections(waypoints);
  }

  /**
   * Draw thick lines connecting waypoints within the same parent node
   * @param {Array} waypoints - Array of waypoints
   */
  drawNodeConnections(waypoints) {
    const nodeGroups = new Map();
    
    waypoints.forEach((waypoint, index) => {
      if (waypoint.parentNode) {
        const nodeKey = waypoint.parentNode;
        if (!nodeGroups.has(nodeKey)) {
          nodeGroups.set(nodeKey, []);
        }
        nodeGroups.get(nodeKey).push({ waypoint, index });
      }
    });

    nodeGroups.forEach((group, nodeKey) => {
      if (group.length < 2) return;

      for (let i = 0; i < group.length - 1; i++) {
        const currentItem = group[i];
        const nextItem = group[i + 1];
        const nextWaypoint = nextItem.waypoint;
        
        const startPos = this.waypointMeshes[currentItem.index].position.clone();
        const endPos = this.waypointMeshes[nextItem.index].position.clone();
        
        if (nextWaypoint.viaPoint) {
          this.drawArc(startPos, nextWaypoint.viaPoint, endPos);
        } else {
          this.drawStraightLine(startPos, endPos);
        }
      }
    });
  }

  /**
   * Draw a straight line between two points
   * @param {THREE.Vector3} start - Start position
   * @param {THREE.Vector3} end - End position
   */
  drawStraightLine(start, end) {
    const direction = new THREE.Vector3().subVectors(end, start);
    const distance = direction.length();
    
    const geometry = new THREE.CylinderGeometry(0.005, 0.005, distance, 8);
    const material = new THREE.MeshBasicMaterial({ 
      color: 0xff8800,
      transparent: true,
      opacity: 1.0
    });
    
    const cylinder = new THREE.Mesh(geometry, material);
    cylinder.position.copy(start).add(direction.clone().multiplyScalar(0.5));
    
    const axis = new THREE.Vector3(0, 1, 0);
    cylinder.quaternion.setFromUnitVectors(axis, direction.normalize());
    
    this.scene.add(cylinder);
    this.pathLines.push(cylinder);
  }

  /**
   * Draw a curved arc through a via-point
   * @param {THREE.Vector3} start - Start position
   * @param {Object} viaPoint - Via-point data with position
   * @param {THREE.Vector3} end - End position
   */
  drawArc(start, viaPoint, end) {
    const via = CoordinateHelper.robotToThreeJS(viaPoint.position);

    const curve = new THREE.CatmullRomCurve3([start, via, end]);
    
    const tubeGeometry = new THREE.TubeGeometry(curve, 50, 0.005, 8, false);
    
    const material = new THREE.MeshBasicMaterial({ 
      color: 0xff8800,
      transparent: true,
      opacity: 1.0
    });
    
    const tube = new THREE.Mesh(tubeGeometry, material);
    this.scene.add(tube);
    this.pathLines.push(tube);
  }

  /**
   * Highlight a waypoint for selection
   * @param {number} index - Waypoint index to highlight
   */
  highlightWaypoint(index) {
    if (index >= 0 && index < this.waypointMeshes.length) {
      const group = this.waypointMeshes[index];
      group.traverse(child => {
        if (child.userData.isCenterSphere && child.material) {
          child.material.emissiveIntensity = 0.8;
        }
      });
      group.scale.set(1.5, 1.5, 1.5);
    }
  }

  /**
   * Unhighlight a waypoint
   * @param {number} index - Waypoint index to unhighlight
   */
  unhighlightWaypoint(index) {
    if (index >= 0 && index < this.waypointMeshes.length) {
      const group = this.waypointMeshes[index];
      group.traverse(child => {
        if (child.userData.isCenterSphere && child.material) {
          child.material.emissiveIntensity = 0.3;
        }
      });
      group.scale.set(1, 1, 1);
    }
  }

  /**
   * Highlight a via-point for selection
   * @param {number} waypointIndex - Parent waypoint index
   */
  highlightViaPoint(waypointIndex) {
    const viaPointGroup = this.viaPointMeshes.find(vm => vm.userData.waypointIndex === waypointIndex);
    if (viaPointGroup) {
      viaPointGroup.traverse(child => {
        if (child.userData.isCenterSphere && child.material) {
          child.material.emissiveIntensity = 0.8;
        }
      });
      viaPointGroup.scale.set(1.5, 1.5, 1.5);
    }
  }

  /**
   * Unhighlight a via-point
   * @param {number} waypointIndex - Parent waypoint index
   */
  unhighlightViaPoint(waypointIndex) {
    const viaPointGroup = this.viaPointMeshes.find(vm => vm.userData.waypointIndex === waypointIndex);
    if (viaPointGroup) {
      viaPointGroup.traverse(child => {
        if (child.userData.isCenterSphere && child.material) {
          child.material.emissiveIntensity = 0.5;
        }
      });
      viaPointGroup.scale.set(1, 1, 1);
    }
  }

  /**
   * Toggle labels visibility
   * @param {boolean} visible - Whether labels should be visible
   */
  setLabelsVisible(visible) {
    this.labelsVisible = visible;
    
    this.waypointMeshes.forEach(mesh => {
      const label = mesh.getObjectByName('waypointLabel');
      if (label) {
        label.visible = visible;
      }
    });
    
    this.viaPointMeshes.forEach(mesh => {
      const label = mesh.getObjectByName('viaPointLabel');
      if (label) {
        label.visible = visible;
      }
    });
  }

  /**
   * Update sphere size multiplier
   * @param {number} multiplier - Size multiplier
   */
  setSphereSize(multiplier) {
    this.sphereSizeMultiplier = multiplier;
  }

  /**
   * Clear all waypoint visualizations
   */
  clear() {
    // Remove waypoint groups
    this.waypointMeshes.forEach(group => {
      this.scene.remove(group);
      group.traverse(child => {
        if (child.geometry) child.geometry.dispose();
        if (child.material) child.material.dispose();
      });
    });
    this.waypointMeshes = [];

    // Remove via-point groups
    this.viaPointMeshes.forEach(group => {
      this.scene.remove(group);
      group.traverse(child => {
        if (child.geometry) child.geometry.dispose();
        if (child.material) child.material.dispose();
      });
    });
    this.viaPointMeshes = [];

    // Remove path lines
    this.clearPaths();
  }

  /**
   * Clear path lines only
   */
  clearPaths() {
    this.pathLines.forEach(line => {
      this.scene.remove(line);
      if (line.geometry) line.geometry.dispose();
      if (line.material) line.material.dispose();
    });
    this.pathLines = [];
  }

  /**
   * Get all selectable meshes (for raycasting)
   * @returns {Array} Array of selectable mesh groups
   */
  getSelectableMeshes() {
    return [...this.waypointMeshes, ...this.viaPointMeshes];
  }
}
